import AccountBoxIcon from "@mui/icons-material/AccountBox";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import {
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { mixpanel } from "common/analytics";
import {
  AddButtonIcon,
  DashboardIcon,
  FieldsIcon,
  InputsOutputsIcon,
} from "common/icons";
import { useTranslation } from "common/locales";
import { selectCurrentFarm, useAppSelector } from "model";
import { selectAllFarmerProgrammeDashboards } from "model/farmerDashboardsSlice";
import React, { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";
import AddNewEventMenu from "./AddNewEventMenu";
import { useAddNewEventDialogs } from "./useAddNewEventDialogs";

import AccountTreeIconConfigurable from "common/icons/AccountTreeIconConfigurable";
import ProgrammesIcon from "common/icons/ProgrammesIcon";

const BottomTabNavigation = () => {
  const [addMenuAnchorEl, setAddMenuAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const currentFarm = useAppSelector(selectCurrentFarm);
  const currentFarmId = currentFarm?.guid;
  const farmerDashboards = useAppSelector(selectAllFarmerProgrammeDashboards);
  const user = useAppSelector((state) => state.user.details);
  const isCorporateUser = !!(
    user?.corporations?.length && user.corporations.length > 0
  );

  const { handleEventTypeClick, dialogs, isAnyDialogOpen } =
    useAddNewEventDialogs();

  // Check if add cover dialog is open (when on /add-covers route)
  const isAddCoverDialogOpen = location.pathname.startsWith("/add-covers");

  // Check if any dialog or the add cover dialog is open
  const isButtonDisabled = isAnyDialogOpen || isAddCoverDialogOpen;

  // Don't show on desktop or if no current farm
  if (!isMobile || (!currentFarmId && !isCorporateUser)) {
    return null;
  }

  const handleAddClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    // Don't open menu if a dialog is already open
    if (isButtonDisabled) {
      return;
    }
    if (addMenuAnchorEl) {
      setAddMenuAnchorEl(null);
    } else {
      setAddMenuAnchorEl(event.currentTarget);
    }
  };

  const handleAddMenuClose = () => {
    setAddMenuAnchorEl(null);
  };

  const handleEventMenuItemClick = (eventType: string) => {
    handleAddMenuClose();
    handleEventTypeClick(eventType);
  };

  const farmerTabs = [
    {
      label: t("go.to.landmanagement.and.grazing.button"),
      icon: <FieldsIcon />,
      value: `/farm/${currentFarmId}`,
      analyticsEvent: "Go to farm view clicked",
    },
    {
      label: t("go.to.inputs.and.outputs"),
      icon: <InputsOutputsIcon />,
      value: "/inputs-and-outputs",
      analyticsEvent: "Go to inputs and outputs clicked",
    },
    {
      label: "",
      icon: (
        <StyledAddButtonWrapper
          onClick={handleAddClick}
          $disabled={isButtonDisabled}
        >
          <AddButtonIcon />
        </StyledAddButtonWrapper>
      ),
      value: "add",
      analyticsEvent: "Add button clicked",
    },
    {
      label: t("go.to.programmes"),
      icon: <ProgrammesIcon />,
      value: "/programmes",
      analyticsEvent: "Go to programmes clicked",
    },
    {
      label: t("go.to.farm.insights"),
      icon: <DashboardIcon />,
      value: "/farm-insights",
      analyticsEvent: "Go to farm insights clicked",
    },
  ];

  const corporateTabs = [
    {
      label: t("go.to.accessible.farms.insights"),
      icon: <DashboardIcon />,
      value: "/accessible-farms-insights",
      analyticsEvent: "Go to farms insights clicked",
    },
    {
      label: t("go.to.project.insights.mobile"),
      icon: <AccountTreeIconConfigurable />,
      value: "/project-insights",
      analyticsEvent: "Go to farms insights clicked",
    },
    {
      label: t("go.to.file.management.button.mobile"),
      icon: <AttachFileIcon />,
      value: "/file-management-overview",
      analyticsEvent: "Go to file management view clicked",
    },
    {
      label: t("settings.title"),
      icon: <AccountBoxIcon />,
      value: "/profile",
      analyticsEvent: "Go to profile view clicked",
    },
  ];

  const tabs = isCorporateUser ? corporateTabs : farmerTabs;
  const defaultTabValue = isCorporateUser
    ? "/project-insights"
    : `/farm/${currentFarmId}`;

  // Get current value based on location
  const getCurrentValue = () => {
    // If any dialog is open, don't select any tab - all icons should be light grey
    if (isButtonDisabled || addMenuAnchorEl) {
      return false;
    }

    const currentPath = location.pathname;

    // Keep farm tab selected for farm-related views
    if (
      currentPath.startsWith("/farm/") ||
      currentPath.startsWith("/animal-group/") ||
      currentPath.startsWith("/edit-animal-group/") ||
      currentPath.startsWith("/add-animal-group") ||
      currentPath.startsWith("/field/") ||
      currentPath.startsWith("/edit-field/") ||
      currentPath.startsWith("/add-field")
    ) {
      return `/farm/${currentFarmId}`;
    }

    // Handle farm insights tab selection
    if (farmerDashboards.length > 0 && currentPath === "/farm-insights") {
      return "/farm-insights";
    }

    return (
      tabs.find((tab) => tab.value === currentPath)?.value ?? defaultTabValue
    );
  };

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    handleAddMenuClose(); // Close menu when changing tabs

    if (newValue === "add") {
      return; // Do nothing as the click is handled by the wrapper
    }

    const tab = tabs.find((tab) => tab.value === newValue);
    if (tab) {
      mixpanel.track(tab.analyticsEvent);
      history.push(newValue);
    }
  };

  return (
    <>
      <Paper
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          paddingBottom: "env(safe-area-inset-bottom)",
          backgroundColor: "white",
        }}
        elevation={8}
      >
        <BottomNavigation
          value={getCurrentValue()}
          onChange={handleChange}
          showLabels
          sx={{
            backgroundColor: "white",
            height: "auto",
            minHeight: "70px",
            "& .MuiBottomNavigationAction-root": {
              color: "#9E9E9E", // Light grey when not selected
              "&.Mui-selected": {
                color: "#000000", // Black when selected
              },
              "&[value='add']": {
                minWidth: "72px",
                "& > .MuiBottomNavigationAction-wrapper": {
                  transform: "translateY(-40%)",
                  marginTop: "16px",
                  minWidth: "72px",
                },
              },
            },
            "& .MuiBottomNavigationAction-label": {
              fontSize: "0.65rem",
              lineHeight: 1.1,
              textAlign: "center",
              whiteSpace: "normal",
              marginTop: "4px",
              "&.Mui-selected": {
                fontSize: "0.65rem",
              },
            },
          }}
        >
          {tabs.map((tab) => (
            <BottomNavigationAction
              key={tab.value}
              label={tab.label}
              icon={tab.icon}
              value={tab.value}
              sx={{
                minWidth: 0,
                padding: "8px 2px",
                "& .MuiSvgIcon-root": {
                  fontSize: "1.4rem",
                  color: "inherit", // Inherit color from parent to ensure it's applied
                },
              }}
            />
          ))}
        </BottomNavigation>
      </Paper>
      <AddNewEventMenu
        anchorEl={addMenuAnchorEl}
        open={Boolean(addMenuAnchorEl)}
        onClose={handleAddMenuClose}
        onEventTypeClick={handleEventMenuItemClick}
        addBottomOffset={true}
      />

      {dialogs}
    </>
  );
};

const StyledAddButtonWrapper = styled.div<{ $disabled?: boolean }>`
  position: relative;
  top: 0px;
  transform: scale(1.6);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: none;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.$disabled ? "none" : "auto")};

  .MuiSvgIcon-root {
    color: ${(props) => (props.$disabled ? "#bdbdbd" : "#757575")};
    font-size: 32px;
  }
`;

export default BottomTabNavigation;
